/**
 * @fileoverview Serviço centralizado de API que utiliza endpoints.json e VITE_API_URL
 * @module ApiService
 * @since 1.0.0
 */

import endpoints from './endpoints.json';
import { API_CONFIG, debugApiConfig } from '../utils/apiConfig.js';

const API_BASE_URL = API_CONFIG.BASE_URL;

// Debug da configuração em desenvolvimento
if (import.meta.env.DEV) {
  debugApiConfig();
  console.log('🔧 API_BASE_URL final:', API_BASE_URL);
}

/**
 * Constrói URL completa para um endpoint
 * @param {string} endpointKey - Chave do endpoint no endpoints.json
 * @param {Object} params - Parâmetros para substituir na URI (ex: {id: 123})
 * @returns {string} URL completa
 */
const buildUrl = (endpointKey, params = {}) => {
  const endpoint = endpoints[endpointKey];
  if (!endpoint) {
    throw new Error(`Endpoint '${endpointKey}' não encontrado`);
  }
  
  let uri = endpoint.uri;
  
  // Substitui parâmetros na URI (ex: :id por valor real)
  Object.keys(params).forEach(key => {
    uri = uri.replace(`:${key}`, params[key]);
  });
  
  return `${API_BASE_URL}${uri}`;
};

/**
 * Faz requisição HTTP para um endpoint
 * @param {string} endpointKey - Chave do endpoint no endpoints.json
 * @param {Object} options - Opções da requisição
 * @param {Object} options.params - Parâmetros para URL
 * @param {Object} options.body - Corpo da requisição
 * @param {Object} options.headers - Headers adicionais
 * @param {boolean} options.credentials - Incluir cookies (padrão: true)
 * @returns {Promise<Response>} Resposta da requisição
 */
export const apiRequest = async (endpointKey, options = {}) => {
  const endpoint = endpoints[endpointKey];
  if (!endpoint) {
    throw new Error(`Endpoint '${endpointKey}' não encontrado`);
  }
  
  const url = buildUrl(endpointKey, options.params);
  const { body, headers = {}, credentials = true, ...fetchOptions } = options;
  
  // Debug para users_update
  if (endpointKey === 'users_update') {
    console.log('Cookies antes da requisição:', document.cookie);
  }
  
  const requestOptions = {
    method: endpoint.method,
    headers: {
      ...API_CONFIG.DEFAULT_HEADERS,
      ...headers,
    },
    ...API_CONFIG.CORS,
    ...fetchOptions,
  };
  
  // Adiciona Authorization header para todas as plataformas
  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    requestOptions.headers.Authorization = `Bearer ${accessToken}`;
  }
  
  if (body && endpoint.method !== 'GET') {
    requestOptions.body = JSON.stringify(body);
  }
  
  return fetch(url, requestOptions);
};

/**
 * Faz requisição e retorna JSON com retry automático para mobile
 * @param {string} endpointKey - Chave do endpoint
 * @param {Object} options - Opções da requisição
 * @returns {Promise<Object>} Dados JSON da resposta
 */
export const apiRequestJson = async (endpointKey, options = {}) => {
  try {
    const response = await apiRequest(endpointKey, options);
    
    if (!response.ok) {
      // Se erro 401 e mobile, tenta refresh automático (força detecção)
      const isMobile = /Mobile|Android|iPhone|iPad/.test(navigator.userAgent);
      if (response.status === 401 && isMobile && endpointKey !== 'auth_refresh_hybrid') {
        try {
          const { refreshToken } = await import('../services/authService.js');
          await refreshToken();
          
          // Retry da requisição original
          const retryResponse = await apiRequest(endpointKey, options);
          if (retryResponse.ok) {
            return retryResponse.json();
          }
        } catch (refreshError) {
          // Se refresh falha, limpa todos os dados
          authManager.clearTokens();
          const { default: dataManager } = await import('../utils/mobileDataManager.js');
          dataManager.clearAll();
          throw new Error('Sessão expirada');
        }
      }
      
      const error = await response.json().catch(() => ({ message: 'Erro na requisição' }));
      throw new Error(error.message || `Erro ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    throw error;
  }
};

export default {
  apiRequest,
  apiRequestJson,
  buildUrl,
};